import React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

// Matches the sticker code ("a-07", "A07", "7"), the name, or the location,
// so someone reading a sticker or naming the equipment both find it.
export function assetMatches(asset, query) {
  const q = (query || "").trim().toLowerCase();
  if (!q) return true;
  const code = (asset.code || "").toLowerCase();
  const compactCode = code.replace(/[^a-z0-9]/g, "");
  const compactQuery = q.replace(/[^a-z0-9]/g, "");
  return (
    code.includes(q) ||
    (compactQuery && compactCode.includes(compactQuery)) ||
    (asset.name || "").toLowerCase().includes(q) ||
    (asset.location || "").toLowerCase().includes(q)
  );
}

function AssetPicker({ assets, value, onChange, disabled }) {
  // The linked asset may be archived (no longer listed); keep it selectable
  // so the field doesn't blank out.
  const options = value && !assets.some((a) => a.uuid === value.uuid) ? [value, ...assets] : assets;

  return (
    <Autocomplete
      options={options}
      value={value}
      onChange={(_e, next) => onChange(next)}
      disabled={disabled}
      size="small"
      isOptionEqualToValue={(o, v) => o.uuid === v.uuid}
      getOptionLabel={(o) => (o.code ? `${o.code} · ${o.name}` : o.name)}
      filterOptions={(opts, state) => opts.filter((o) => assetMatches(o, state.inputValue))}
      noOptionsText="No matching assets"
      renderOption={(optionProps, o) => (
        <li {...optionProps} key={o.uuid}>
          <div className="flex items-center gap-3 w-full">
            <span className="w-12 flex-shrink-0 text-xs font-semibold text-violet-700 tabular-nums">
              {o.code}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{o.name}</p>
              <p className="text-xs text-slate-500 truncate">
                {[o.location, [o.manufacturer, o.model_number].filter(Boolean).join(" ")]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </div>
            {o.stats?.open_count > 0 && (
              <span className="text-xs text-violet-700 whitespace-nowrap">
                {o.stats.open_count} open
              </span>
            )}
          </div>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search by name, area, or sticker code"
          sx={{
            // Same 45px field as the drawer's other dropdowns (selectSx).
            "& .MuiOutlinedInput-root": {
              backgroundColor: "white",
              borderRadius: "8px",
              height: "45px",
              paddingTop: 0,
              paddingBottom: 0,
              "&.Mui-focused fieldset": { borderColor: "#6d28d9" },
            },
            // Placeholder in the same slate as the drawer's "Unassigned".
            "& .MuiOutlinedInput-input::placeholder": { color: "#94a3b8", opacity: 1 },
          }}
        />
      )}
    />
  );
}

export default AssetPicker;
